import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { scrollToId } from "@/lib/scroll";

const HERO_IMG =
  "https://images.unsplash.com/photo-1748063578185-3d68121b11ff?crop=entropy&cs=srgb&fm=jpg&q=85";

const LINES = ["BUILD.", "RENOVATE.", "TRANSFORM."];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative flex min-h-screen items-center overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Luxury renovated property exterior at dusk — Tyrell Property Services, Essex"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#050B14]/85 via-[#050B14]/55 to-[#050B14]" />
      <div className="absolute inset-0 architectural-grid opacity-60" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pt-28 pb-20 sm:px-8">
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-8 flex items-center gap-4"
        >
          <span className="h-px w-14 bg-gradient-to-r from-[#C5A059] to-transparent" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.35em] text-[#C5A059]">
            Tyrell Property Services — Essex
          </span>
        </motion.div>

        <h1 className="font-serif text-5xl font-light uppercase leading-[1.02] tracking-tight text-white sm:text-7xl lg:text-8xl">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.05, delay: 0.35 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
              >
                {line.slice(0, -1)}
                <span className="gold-text-gradient">.</span>
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.0 }}
          className="mt-8 max-w-xl text-base font-light leading-relaxed text-slate-300 sm:text-lg"
        >
          Premium property services, project management and construction solutions
          delivered with precision.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15 }}
          className="mt-10 flex flex-col gap-4 sm:flex-row"
        >
          <button
            data-testid="hero-quote-button"
            onClick={() => scrollToId("#contact")}
            className="group relative overflow-hidden bg-[#C5A059] px-9 py-4 text-xs font-bold uppercase tracking-[0.25em] text-[#050B14] transition-transform duration-300 hover:-translate-y-0.5"
          >
            <span className="relative z-10">Request a Quote</span>
            <span className="absolute inset-0 -translate-x-full bg-[#E6CA65] transition-transform duration-500 group-hover:translate-x-0" />
          </button>
          <button
            data-testid="hero-work-button"
            onClick={() => scrollToId("#projects")}
            className="border border-white/30 px-9 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-white transition-all duration-300 hover:-translate-y-0.5 hover:border-[#C5A059] hover:text-[#C5A059]"
          >
            View Our Work
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          data-testid="hero-trust-line"
          className="mt-12 flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] uppercase tracking-[0.3em] text-slate-400"
        >
          <span>Quality Work</span>
          <span className="h-1 w-1 rotate-45 bg-[#C5A059]" />
          <span>Reliable Team</span>
          <span className="h-1 w-1 rotate-45 bg-[#C5A059]" />
          <span>Your Vision, Our Priority</span>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
      >
        <div className="h-14 w-px animate-scroll-pulse bg-gradient-to-b from-[#C5A059] to-transparent" />
      </motion.div>
    </section>
  );
};
